import { PARAMS, ROUTES } from '@/constants/routes';

// ================================
// Route Builders
// ================================
//
// These functions centralize dynamic URL construction so feature code can
// avoid string concatenation and remain aligned with the canonical templates
// defined in `ROUTES`.

/**
 * Build the Sales Rep certification overview URL.
 *
 * Replaces the `{certificationId}` placeholder in the route template with a
 * concrete certification UUID.
 *
 * @param certificationId Certification UUID used in the path segment.
 * @returns A concrete certification overview route.
 */
export const buildCertificationUrl = (certificationId: string) => {
  const route = ROUTES.CERTIFICATION_OVERVIEW;
  return route.replace(`{${PARAMS.CERTIFICATION_ID}}`, certificationId);
};

/**
 * Build the simulation preparation URL.
 *
 * This route is nested under a certification and simulation context, so both
 * dynamic placeholders must be resolved before navigation.
 *
 * @param certificationId Certification UUID for the parent path segment.
 * @param simulationId Simulation UUID for the nested path segment.
 * @returns A concrete simulation prep route.
 */
export const buildSimulationPrepUrl = (certificationId: string, simulationId: string) => {
  const route = ROUTES.SIMULATION_PREP;
  return route
    .replace(`{${PARAMS.CERTIFICATION_ID}}`, certificationId)
    .replace(`{${PARAMS.SIMULATION_ID}}`, simulationId);
};

/**
 * Build the simulation roleplay URL.
 *
 * Uses the same nested route structure as prep, but targets the roleplay
 * destination.
 *
 * @param certificationId Certification UUID for the parent path segment.
 * @param simulationId Simulation UUID for the nested path segment.
 * @returns A concrete simulation roleplay route.
 */
export const buildRoleplayUrl = (certificationId: string, simulationId: string) => {
  const route = ROUTES.SIMULATION_ROLEPLAY;
  return route
    .replace(`{${PARAMS.CERTIFICATION_ID}}`, certificationId)
    .replace(`{${PARAMS.SIMULATION_ID}}`, simulationId);
};

/**
 * Build the simulation score URL.
 *
 * Optionally appends query parameters (for example `sessionId` or source
 * metadata) when provided.
 *
 * @param certificationId Certification UUID for the parent path segment.
 * @param simulationId Simulation UUID for the nested path segment.
 * @param queryParams Optional query-string key/value map.
 * @returns A concrete simulation score route, with optional query string.
 */
export const buildSimulationScoreUrl = (
  certificationId: string,
  simulationId: string,
  queryParams?: Record<string, string>,
) => {
  const route = ROUTES.SIMULATION_SCORE;
  let url = route
    .replace(`{${PARAMS.CERTIFICATION_ID}}`, certificationId)
    .replace(`{${PARAMS.SIMULATION_ID}}`, simulationId);

  if (queryParams) {
    // URLSearchParams handles URI encoding and string serialization.
    const queryString = new URLSearchParams(queryParams).toString();
    url += `?${queryString}`;
  }

  return url;
};

/**
 * Build the simulation certificate URL.
 *
 * @param certificationId Certification UUID used in the path segment.
 * @returns A concrete certificate route for the certification.
 */
export const buildSimulationCertUrl = (certificationId: string) => {
  const route = ROUTES.SIMULATION_CERT;
  return route.replace(`{${PARAMS.CERTIFICATION_ID}}`, certificationId);
};

/**
 * Build the manager certification management URL.
 *
 * - When no ID is provided, this returns the certifications collection route.
 * - When an ID is provided, this returns a child detail route.
 *
 * @param certificationId Optional certification UUID for detail routes.
 * @returns Collection or detail manager certification route.
 */
export const buildManagerCertificationUrl = (certificationId?: string) => {
  const route = ROUTES.MANAGER_CERTIFICATIONS;

  if (certificationId) {
    return `${route}/${certificationId}`;
  }

  return route;
};

/**
 * Build the manager user management URL.
 *
 * Supports:
 * - Collection route: `/manager/users`
 * - Detail route: `/manager/users/:userId`
 * - Detail route with query string for tab/deep-link state
 *
 * @param userId Optional user UUID for a detail route.
 * @param queryParams Optional query-string key/value map.
 * @returns Manager users route with optional detail segment and query string.
 */
export const buildManagerUserUrl = (userId?: string, queryParams?: Record<string, string>) => {
  const route = ROUTES.MANAGER_USERS;
  let url = userId ? `${route}/${userId}` : route;

  if (queryParams) {
    // URLSearchParams handles URI encoding and string serialization.
    const queryString = new URLSearchParams(queryParams).toString();
    url += `?${queryString}`;
  }

  return url;
};

// ================================
// Parameter Extraction
// ================================
//
// These helpers normalize route/query values into predictable shapes so
// consumers can avoid repeated runtime type checks.

/**
 * Extract and normalize known dynamic route parameters.
 *
 * Next.js route params can contain `string`, `string[]`, `undefined`, or be
 * absent entirely. This helper intentionally narrows each supported parameter
 * to `string | null` for stable downstream consumption.
 *
 * @param params Raw route params object from `useParams()` (or equivalent).
 * @returns Known route params normalized to nullable strings.
 */
export const extractRouteParams = (params?: Record<string, string | string[]> | null) => {
  // Fall back to an empty object so property access is always safe.
  const routeParams = params ?? {};

  // Shared extractor to enforce one normalization rule for every key.
  const helper = (param: string) => {
    const value = routeParams[param];
    return typeof value === 'string' ? value : null;
  };

  // Explicitly list supported params here so the output type is stable and
  // predictable for consumers. This also provides a single place to manage any
  // future additions or changes to route params.
  return {
    [PARAMS.CERTIFICATION_ID]: helper('certificationId'),
    [PARAMS.SESSION_ID]: helper('sessionId'),
    [PARAMS.SIMULATION_ID]: helper('simulationId'),
    [PARAMS.USER_ID]: helper('userId'),
  };
};

/**
 * Convert URL query parameters into a plain object map.
 *
 * Accepts either:
 * - a raw query string (e.g. `?sessionId=123`)
 * - an existing `URLSearchParams` instance
 *
 * Notes:
 * - If duplicate keys exist, later values overwrite earlier ones.
 * - Output is always a flat `Record<string, string>`.
 *
 * @param searchParams Query source as string or URLSearchParams.
 * @returns Plain object containing query key/value pairs.
 */
export const extractQueryParams = (
  searchParams: URLSearchParams | string,
): Record<string, string> => {
  // prettier-ignore
  const params =
    typeof searchParams === 'string'
      ? new URLSearchParams(searchParams)
      : searchParams;

  // Accumulate key/value pairs into a plain object for easy consumer access.
  const result: Record<string, string> = {};

  params.forEach((value, key) => {
    // URLSearchParams may contain repeated keys; the latest value wins.
    result[key] = value;
  });

  // Return the result
  return result;
};
